import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Send, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

interface DistributeForm {
  user_id: string;
  amount: string; // Agent types amount (Tk)
  reference: string;
}

export function AgentDistributePage() {
  const [recipient, setRecipient] = useState<any | null>(null);
  const [searching, setSearching] = useState(false);
  const [suggestedPoints, setSuggestedPoints] = useState<number>(0);
  const qc = useQueryClient();

  const { register, handleSubmit, watch, formState: { errors }, reset } = useForm<DistributeForm>();
  const userIdValue = watch("user_id");
  const amountValue = watch("amount");

  // 1. Live Search for Normal User
  useEffect(() => {
    if (!userIdValue || userIdValue.length < 5) {
      setRecipient(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await get<any>(`/agent/users/search?user_id=${userIdValue}`);
        setRecipient(res);
      } catch {
        setRecipient(null);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [userIdValue]);

  // 2. Automatically calculate points based on amount
  useEffect(() => {
    if (!amountValue || Number(amountValue) <= 0) {
      setSuggestedPoints(0);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await get<any>(`/agent/points/suggest?amount=${amountValue}`);
        // Ensure it's a round integer (floor) - komey round figure hobe, bere noy
        setSuggestedPoints(Math.floor(res.suggested_points));
      } catch {
        setSuggestedPoints(0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [amountValue]);

  // 3. Submit Distribution
  const distribute = useMutation({
    mutationFn: (d: DistributeForm) =>
      post<any>("/agent/points/distribute", {
        user_id: d.user_id,
        points: suggestedPoints, // Send the calculated integer points
        reference: d.reference || `Purchase amount: ${d.amount}`,
      }, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => {
      toast.success("Points successfully distributed to Customer!");
      reset();
      setRecipient(null);
      setSuggestedPoints(0);
      qc.invalidateQueries({ queryKey: ["agent-profile"] });
      qc.invalidateQueries({ queryKey: ["agent-history"] });
    },
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Distribute to Customer</h1>
        <p className="text-sm text-surface-500 mt-1">
          Enter purchase amount. The system will automatically calculate and send points to the customer.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Distribution Details</CardTitle>
          <Send className="h-4 w-4 text-surface-400" />
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit((d) => distribute.mutate(d))} className="space-y-6">
            
            <div className="space-y-3">
              <Input
                label="Customer User ID"
                placeholder="Enter Customer's User ID (e.g., USR000003)"
                leftIcon={<Search className="h-4 w-4" />}
                error={errors.user_id?.message}
                {...register("user_id", { required: "Customer ID is required" })}
              />

              <div className="min-h-[60px]">
                {searching ? (
                  <div className="text-sm text-surface-500 animate-pulse">Searching customer...</div>
                ) : recipient ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-success-200 bg-success-50">
                    <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                    <div>
                      <div className="text-sm font-bold text-success-900">{recipient.full_name}</div>
                      <div className="text-xs text-success-700">Customer Account</div>
                    </div>
                  </div>
                ) : userIdValue && userIdValue.length >= 5 ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-danger-200 bg-danger-50">
                    <ShieldAlert className="h-5 w-5 text-danger-600 shrink-0" />
                    <div className="text-sm text-danger-900">Customer not found.</div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Amount and Point Calculation */}
            <div className="grid sm:grid-cols-2 gap-4 items-start bg-surface-50 p-4 rounded-xl border border-surface-200">
              <Input
                label="Purchase Amount (Tk)"
                type="number"
                min="1"
                placeholder="Enter total amount"
                error={errors.amount?.message}
                {...register("amount", { 
                  required: "Amount is required",
                  validate: (val) => Number(val) > 0 || "Must be positive"
                })}
              />

              <div>
                <label className="block text-sm font-medium text-surface-800 mb-1.5">
                  Points to Generate
                </label>
                <div className="h-10 px-4 bg-white border border-surface-300 rounded-lg flex items-center justify-between font-mono font-bold text-lg text-brand-700">
                  <span>{suggestedPoints}</span>
                  <span className="text-xs text-surface-400 font-sans font-normal uppercase">Pts</span>
                </div>
                <p className="mt-1.5 text-2xs text-surface-500">
                  Auto-calculated (rounded down) based on admin conversion rate.
                </p>
              </div>
            </div>

            <Input
              label="Reference / Invoice No. (Optional)"
              placeholder="e.g. INV-100234"
              {...register("reference")}
            />

            <Button 
              type="submit" 
              size="lg" 
              fullWidth 
              loading={distribute.isPending}
              disabled={!recipient || suggestedPoints <= 0}
            >
              Send {suggestedPoints} Points to Customer
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}